import { FormEvent, useEffect, useRef, useState } from 'react';
import { createSupportTicket, readTicketLogin, TicketApiError, TicketCategory, TicketReceipt, TicketSubmission } from '../../services/supportTicketApi';

const messages = {
  en: { heading: 'Create a support ticket', category: 'Topic', otp: 'OTP / sign-in', donation: 'Donation request', mosque: 'Mosque services', other: 'Other',
    subject: 'Subject', description: 'Describe your problem', email: 'Email (optional)', phone: 'Phone (optional, international format)',
    privacy: 'Include only details needed for support. Do not include passwords, verification codes or identity documents.',
    contact: 'For follow-up, provide an email or phone number you can be reached on.', submit: 'Submit ticket', retry: 'Retry submission', cancel: 'Cancel',
    submitting: 'Submitting…', success: 'Your ticket has been created.', reference: 'Ticket reference', status: 'Status',
    open: 'Open', in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed',
    invalid: 'Check the required fields, their lengths, and the contact details.',
    auth: 'Your sign-in could not be verified. Sign in again before submitting; we have not submitted this as a guest.',
    changed: 'Your sign-in changed during this submission. Restore the same sign-in before retrying.',
    conflict: 'This request ID has already been used with different details. Contact support on 1933 before submitting another ticket.',
    unavailable: 'We could not confirm the submission. Retry with the same details to avoid a duplicate ticket.',
    limited: 'Too many requests. Please wait before retrying.', wait: 'seconds remaining',
    configuration: 'Signed-in ticket creation is unavailable. Please try later or contact support on 1933.',
    uuid: 'This browser cannot create a secure request ID. Use a supported browser on HTTPS or localhost.',
    locked: 'These details are kept unchanged until we can confirm the result.' },
  ar: { heading: 'إنشاء تذكرة دعم', category: 'الموضوع', otp: 'رمز التحقق / تسجيل الدخول', donation: 'طلب تبرع', mosque: 'خدمات المساجد', other: 'أخرى',
    subject: 'عنوان التذكرة', description: 'صف المشكلة', email: 'البريد الإلكتروني (اختياري)', phone: 'رقم الجوال (اختياري، بالصيغة الدولية)',
    privacy: 'اذكر التفاصيل اللازمة للدعم فقط. لا تضف كلمات مرور أو رموز تحقق أو وثائق هوية.',
    contact: 'للمتابعة، أدخل بريداً إلكترونياً أو رقم جوال يمكن التواصل معك من خلاله.', submit: 'إرسال التذكرة', retry: 'إعادة محاولة الإرسال', cancel: 'إلغاء',
    submitting: 'جارٍ الإرسال…', success: 'تم إنشاء تذكرتك.', reference: 'رقم التذكرة', status: 'الحالة',
    open: 'مفتوحة', in_progress: 'قيد المعالجة', resolved: 'تم الحل', closed: 'مغلقة',
    invalid: 'تحقق من الحقول المطلوبة وطول النصوص وبيانات التواصل.',
    auth: 'تعذر التحقق من تسجيل دخولك. سجّل الدخول مجدداً قبل الإرسال؛ لم نرسل التذكرة كزائر.',
    changed: 'تغير تسجيل دخولك أثناء هذا الإرسال. استعد تسجيل الدخول نفسه قبل إعادة المحاولة.',
    conflict: 'استُخدم معرف الطلب مع تفاصيل مختلفة. تواصل مع الدعم على 1933 قبل إرسال تذكرة أخرى.',
    unavailable: 'تعذر تأكيد الإرسال. أعد المحاولة بالتفاصيل نفسها لتجنب إنشاء تذكرة مكررة.',
    limited: 'طلبات كثيرة. يرجى الانتظار قبل إعادة المحاولة.', wait: 'ثانية متبقية',
    configuration: 'إنشاء التذاكر للمستخدمين المسجلين غير متاح حالياً. حاول لاحقاً أو تواصل مع الدعم على 1933.',
    uuid: 'لا يستطيع هذا المتصفح إنشاء معرف طلب آمن. استخدم متصفحاً مدعوماً عبر HTTPS أو localhost.',
    locked: 'نحتفظ بهذه التفاصيل دون تغيير حتى نتمكن من تأكيد النتيجة.' },
};

type Props = { language: 'ar' | 'en'; apiBaseUrl: string; category: TicketCategory; onCancel: () => void; onLockChange: (locked: boolean) => void };
export default function TicketForm({ language, apiBaseUrl, category: initialCategory, onCancel, onLockChange }: Props) {
  const t = messages[language];
  const [category, setCategory] = useState<TicketCategory>(initialCategory);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState<keyof typeof messages.en | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [receipt, setReceipt] = useState<TicketReceipt | null>(null);
  const active = useRef<AbortController | null>(null);
  const subjectInput = useRef<HTMLInputElement>(null);
  const confirmation = useRef<HTMLDivElement>(null);
  // The retry key and original payload live only in memory, including after closing/reopening the panel.
  const attempt = useRef<{ submission: TicketSubmission; login: string | null; uncertain?: boolean } | null>(null);

  useEffect(() => { subjectInput.current?.focus(); return () => { active.current?.abort(); active.current = null; }; }, []);
  useEffect(() => { onLockChange(locked || busy); return () => onLockChange(false); }, [locked, busy, onLockChange]);
  useEffect(() => { if (receipt) confirmation.current?.focus(); }, [receipt]);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(n => Math.max(0, n - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (active.current || cooldown || receipt || error === 'conflict') return;
    setError(null);
    if (!attempt.current) {
      if (!subject.trim() || Array.from(subject.trim()).length > 200 || !description.trim() || Array.from(description.trim()).length > 4000 ||
          subject.includes('\0') || description.includes('\0') || email.includes('\0') ||
          (email.trim() && (email.trim().length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))) ||
          (phone.trim() && !/^\+[1-9][0-9]{7,14}$/.test(phone.trim()))) { setError('invalid'); return; }
      if (!window.crypto?.randomUUID) { setError('uuid'); return; }
      try {
        attempt.current = { login: readTicketLogin(), submission: { requestId: window.crypto.randomUUID(), category,
          subject: subject.trim(), description: description.trim(), contactEmail: email.trim() || null, contactPhone: phone.trim() || null, language } };
      } catch { setError('auth'); return; }
    } else {
      // Never retry a signed-in request as a guest after logout or send a saved token for a different login.
      try { if (readTicketLogin() !== attempt.current.login) { setError('changed'); return; } }
      catch { setError('changed'); return; }
    }
    const controller = new AbortController(); active.current = controller;
    setBusy(true); setLocked(true);
    const timeout = window.setTimeout(() => controller.abort(), 25000);
    try {
      const result = await createSupportTicket(apiBaseUrl, attempt.current.submission, attempt.current.login, controller.signal);
      if (active.current !== controller) return;
      setReceipt(result); setLocked(false); attempt.current = null;
    } catch (failure) {
      if (active.current !== controller) return;
      const status = failure instanceof TicketApiError ? failure.status : 0;
      setError(status === 401 ? 'auth' : status === 409 ? 'conflict' : status === 429 ? 'limited' :
        [400, 413, 415].includes(status) ? 'invalid' : 'unavailable');
      if (failure instanceof TicketApiError && [429, 503].includes(status)) setCooldown(failure.retryAfter);
      // Definite validation/authentication rejection did not save a ticket. Let the visitor correct it.
      // For network/5xx/unknown results keep the exact key, login and payload for a safe retry.
      if ([400, 401, 413, 415, 429].includes(status) && !attempt.current?.uncertain) {
        attempt.current = null; setLocked(false);
      } else if (attempt.current) {
        // A later rate-limit/auth error cannot prove that an earlier timed-out attempt was not saved.
        attempt.current.uncertain = true;
      }
    } finally {
      window.clearTimeout(timeout);
      if (active.current === controller) { active.current = null; setBusy(false); }
    }
  }

  if (receipt) return <div className="support-ticket__success" ref={confirmation} tabIndex={-1} role="status">
    <strong>{t.success}</strong><p>{t.reference}</p><bdi className="support-ticket__reference">{receipt.ticketReference}</bdi>
    <p>{t.status}: {t[receipt.status as 'open' | 'in_progress' | 'resolved' | 'closed']}</p>
  </div>;

  return <form className="support-ticket" onSubmit={submit} aria-label={t.heading} aria-busy={busy}>
    <h3>{t.heading}</h3>
    <fieldset disabled={busy || locked}>
      <label>{t.category}<select value={category} onChange={e => setCategory(e.target.value as TicketCategory)}>
        {(['otp', 'donation', 'mosque', 'other'] as const).map(value => <option key={value} value={value}>{t[value]}</option>)}
      </select></label>
      <label>{t.subject}<input ref={subjectInput} value={subject} onChange={e => setSubject(e.target.value)} required maxLength={200} /></label>
      <label>{t.description}<textarea value={description} onChange={e => setDescription(e.target.value)} required maxLength={4000} rows={4} /></label>
      <label>{t.email}<input type="email" dir="ltr" value={email} onChange={e => setEmail(e.target.value)} maxLength={254} autoComplete="email" /></label>
      <label>{t.phone}<input type="tel" dir="ltr" value={phone} onChange={e => setPhone(e.target.value)} maxLength={16} placeholder="+966…" autoComplete="tel" /></label>
    </fieldset>
    <p>{t.contact}</p><p>{t.privacy}</p>
    {error && <p role="alert">{t[error]}</p>}
    {locked && !busy && <p>{t.locked}</p>}
    {cooldown > 0 && <p role="status">{cooldown} {t.wait}</p>}
    <button type="submit" className="support-chat__restart" disabled={busy || cooldown > 0 || error === 'conflict'}>
      {busy ? t.submitting : attempt.current ? t.retry : t.submit}
    </button>
    <button type="button" onClick={onCancel} disabled={busy || locked}>{t.cancel}</button>
  </form>;
}


